/**
 * Export/Import controls for trip data.
 * Export formats: JSON, PDF, JPG
 * Export destinations: user's computer or Fias file system
 *
 * JPG export supports three layouts:
 *   - single:    one long-column image (legacy behavior)
 *   - paginated: list-style content split into one page per day/transit
 *   - letter:    print-style 8.5x11 pages (cover + calendar + day/transit pages)
 *
 * Multi-page JPG output can be delivered as:
 *   - zip:        single .zip containing page-NN.jpg files
 *   - individual: sequential per-file downloads
 *   - stitched:   pages re-composited into one tall JPG with separators
 */

import { useState, useRef, useCallback } from 'react';
import { Download, Upload, Loader2, Share2, Link as LinkIcon, ImageDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { captureError } from '@/lib/telemetry';
import api from '@/lib/api';
import type {
  TravelTripResponse,
  TravelItemResponse,
  TravelEventResponse,
  TravelAttachmentResponse,
} from '../api';
import { TravelCalendarView } from './TravelCalendarView';
import {
  formatDayDate,
  formatDateRange,
  getEventTypeColor,
  EVENT_TYPE_LABELS,
  TRANSIT_MODE_EMOJI,
} from './TravelHelpers';
import { formatTime } from './TravelSettings';
import { captureCanvasAsJpegBlob, canvasToPdfImageBytes } from '../lib/export-capture';

type ExportFormat = 'json' | 'pdf' | 'jpg';
type ExportDestination = 'computer' | 'fias';
type JpgLayout = 'single' | 'paginated' | 'letter';
type MultiPageDelivery = 'zip' | 'individual' | 'stitched';

interface ExportSections {
  calendar: boolean;
  itinerary: boolean;
  accommodations: boolean;
  transit: boolean;
  notes: boolean;
  photos: boolean;
}

const ALL_SECTIONS_ON: ExportSections = {
  calendar: true,
  itinerary: true,
  accommodations: true,
  transit: true,
  notes: true,
  photos: true,
};

const SECTION_LABELS: Array<{ key: keyof ExportSections; label: string }> = [
  { key: 'calendar', label: 'Calendar overview' },
  { key: 'itinerary', label: 'Day-by-day itinerary' },
  { key: 'accommodations', label: 'Accommodations' },
  { key: 'transit', label: 'Transit segments' },
  { key: 'notes', label: 'Event notes' },
  { key: 'photos', label: 'Photos / attachments' },
];

// Letter page dimensions at 100dpi. html2canvas captures at scale: 2 → 1700x2200 output.
const LETTER_W = 850;
const LETTER_H = 1100;
const LETTER_PAD = 50;
// Paginated-list page width matches the legacy single-image width.
const LIST_W = 900;
const LIST_PAD = 40;

type PageDescriptor =
  | { kind: 'single' }
  | { kind: 'paginated-calendar' }
  | { kind: 'paginated-day'; item: TravelItemResponse }
  | { kind: 'paginated-transit'; item: TravelItemResponse }
  | { kind: 'letter-cover' }
  | { kind: 'letter-calendar' }
  | { kind: 'letter-day'; item: TravelItemResponse }
  | { kind: 'letter-transit'; item: TravelItemResponse };

export function buildPagePlan(
  layout: JpgLayout,
  sections: ExportSections,
  items: TravelItemResponse[],
): PageDescriptor[] {
  if (layout === 'single') return [{ kind: 'single' }];
  const pages: PageDescriptor[] = [];
  if (layout === 'letter') pages.push({ kind: 'letter-cover' });
  if (sections.calendar) {
    pages.push({ kind: layout === 'letter' ? 'letter-calendar' : 'paginated-calendar' });
  }
  if (sections.itinerary) {
    for (const item of items) {
      if (item.itemType === 'day') {
        pages.push(
          layout === 'letter' ? { kind: 'letter-day', item } : { kind: 'paginated-day', item },
        );
      } else if (item.itemType === 'transit' && sections.transit) {
        pages.push(
          layout === 'letter'
            ? { kind: 'letter-transit', item }
            : { kind: 'paginated-transit', item },
        );
      }
    }
  }
  return pages;
}

/** Static attachment renderer for export — uses pre-fetched data URLs, no async effects */
function ExportAttachments({
  eventAttachments,
  imageDataUrls,
  thumbnail = false,
}: {
  eventAttachments: TravelAttachmentResponse[];
  imageDataUrls: Map<string, string>;
  thumbnail?: boolean;
}) {
  if (eventAttachments.length === 0) return null;
  if (thumbnail) {
    const withUrl = eventAttachments.filter((a) => imageDataUrls.get(a.fileId));
    if (withUrl.length === 0) return null;
    return (
      <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
        {withUrl.slice(0, 4).map((att) => (
          <img
            key={att.attachmentId}
            src={imageDataUrls.get(att.fileId)!}
            alt={att.fileName || ''}
            style={{
              width: '90px',
              height: '60px',
              objectFit: 'cover',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
            }}
          />
        ))}
      </div>
    );
  }
  return (
    <div style={{ marginTop: '8px' }}>
      {eventAttachments.map((att) => {
        const dataUrl = imageDataUrls.get(att.fileId);
        if (dataUrl) {
          return (
            <div key={att.attachmentId} style={{ marginBottom: '8px' }}>
              <img
                src={dataUrl}
                alt={att.fileName || ''}
                style={{
                  maxHeight: '300px',
                  maxWidth: '100%',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                }}
              />
              <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                {att.fileName}
              </div>
            </div>
          );
        }
        return (
          <div
            key={att.attachmentId}
            style={{ fontSize: '14px', color: '#888', marginBottom: '4px' }}
          >
            📎 {att.fileName || att.fileId}
          </div>
        );
      })}
    </div>
  );
}

function buildEventsByItem(events: TravelEventResponse[]): Map<string, TravelEventResponse[]> {
  const map = new Map<string, TravelEventResponse[]>();
  for (const ev of events) {
    const list = map.get(ev.itemId) || [];
    list.push(ev);
    map.set(ev.itemId, list);
  }
  return map;
}

function buildAttsByEvent(
  attachments: TravelAttachmentResponse[],
): Map<string, TravelAttachmentResponse[]> {
  const map = new Map<string, TravelAttachmentResponse[]>();
  for (const att of attachments) {
    if (!att.eventId) continue;
    const list = map.get(att.eventId) || [];
    list.push(att);
    map.set(att.eventId, list);
  }
  return map;
}

/** Single day card — reused by list (single + paginated) layouts. */
function DayCard({
  trip,
  item,
  dayEvents,
  attsByEvent,
  imageDataUrls,
  sections,
}: {
  trip: TravelTripResponse;
  item: TravelItemResponse;
  dayEvents: TravelEventResponse[];
  attsByEvent: Map<string, TravelAttachmentResponse[]>;
  imageDataUrls: Map<string, string>;
  sections: ExportSections;
}) {
  return (
    <div style={{ marginBottom: '36px' }}>
      <div
        style={{
          borderBottom: '3px solid #e5e7eb',
          paddingBottom: '8px',
          marginBottom: '14px',
        }}
      >
        <div style={{ fontSize: '15px', color: '#2563eb', fontWeight: 500 }}>
          {formatDayDate(trip.startDate, item.dayNumber ?? 1)}
        </div>
        <div style={{ fontSize: '26px', fontWeight: 'bold', marginTop: '2px' }}>
          {item.dayTitle || `Day ${item.dayNumber}`}
        </div>
        {item.city && (
          <div style={{ fontSize: '16px', color: '#666', marginTop: '2px' }}>
            {item.city}
            {item.country ? `, ${item.country}` : ''}
          </div>
        )}
      </div>
      {sections.accommodations && item.accommodationName && (
        <div style={{ fontSize: '16px', marginBottom: '12px', marginLeft: '16px' }}>
          🏨 <strong>{item.accommodationName}</strong>
          {item.accommodationAddress && (
            <span style={{ color: '#666' }}> — {item.accommodationAddress}</span>
          )}
        </div>
      )}
      {dayEvents.length > 0 ? (
        <div style={{ marginLeft: '16px' }}>
          {dayEvents.map((ev) => {
            const color = getEventTypeColor(ev.eventType);
            const evAtts = attsByEvent.get(ev.eventId) || [];
            return (
              <div
                key={ev.eventId}
                style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '12px',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: '60px',
                    textAlign: 'right',
                    fontSize: '15px',
                    color: '#888',
                    flexShrink: 0,
                    fontFamily: 'monospace',
                  }}
                >
                  {ev.time ? formatTime(ev.time) : '—'}
                </div>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    marginTop: '7px',
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '17px', fontWeight: 500 }}>{ev.title}</span>
                  <span
                    style={{
                      fontSize: '12px',
                      marginLeft: '8px',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      backgroundColor: color + '20',
                      color,
                    }}
                  >
                    {EVENT_TYPE_LABELS[ev.eventType]}
                  </span>
                  {sections.notes && ev.notes && (
                    <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                      {ev.notes}
                    </div>
                  )}
                  {sections.photos && (
                    <ExportAttachments eventAttachments={evAtts} imageDataUrls={imageDataUrls} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: '15px', color: '#bbb', marginLeft: '16px', fontStyle: 'italic' }}>
          No events planned
        </div>
      )}
    </div>
  );
}

/** Transit segment — reused by list layouts. */
function TransitRow({ item }: { item: TravelItemResponse }) {
  return (
    <div style={{ margin: '24px 0', textAlign: 'center', color: '#666', fontSize: '16px' }}>
      {item.transitMode && TRANSIT_MODE_EMOJI[item.transitMode]} {item.transitFrom || '?'} →{' '}
      {item.transitTo || '?'}
      {item.transitDeparture && ` (${item.transitDeparture})`}
    </div>
  );
}

/** Letter-size cover: title, dates, summary stats. */
function LetterCover({
  trip,
  dayCount,
  eventCount,
  pageNumber,
  totalPages,
}: {
  trip: TravelTripResponse;
  dayCount: number;
  eventCount: number;
  pageNumber: number;
  totalPages: number;
}) {
  return (
    <LetterFrame pageNumber={pageNumber} totalPages={totalPages} headerTitle={trip.title}>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <h1 style={{ fontSize: '52px', fontWeight: 'bold', margin: 0, lineHeight: 1.1 }}>
          {trip.title}
        </h1>
        <div style={{ fontSize: '22px', color: '#666', marginTop: '20px' }}>
          {formatDateRange(trip.startDate, dayCount)}
        </div>
        <div style={{ fontSize: '17px', color: '#888', marginTop: '12px' }}>
          {dayCount} days · {eventCount} events
        </div>
      </div>
    </LetterFrame>
  );
}

/** Letter-size frame with header bar and footer page number. */
function LetterFrame({
  children,
  pageNumber,
  totalPages,
  headerTitle,
  headerRight,
}: {
  children: React.ReactNode;
  pageNumber: number;
  totalPages: number;
  headerTitle: string;
  headerRight?: string;
}) {
  return (
    <div
      data-export-page
      style={{
        width: `${LETTER_W}px`,
        height: `${LETTER_H}px`,
        backgroundColor: '#ffffff',
        color: '#000000',
        padding: `${LETTER_PAD}px`,
        fontFamily: 'system-ui, sans-serif',
        position: 'relative',
        boxSizing: 'border-box',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        pageBreakAfter: 'always',
        breakAfter: 'page',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          fontSize: '12px',
          color: '#888',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: '6px',
          marginBottom: '14px',
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '60%',
          }}
        >
          {headerTitle}
        </span>
        <span>{headerRight}</span>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {children}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: '#aaa',
          textAlign: 'center',
          marginTop: '8px',
        }}
      >
        {pageNumber} / {totalPages}
      </div>
    </div>
  );
}

/** Letter-size day page. */
function LetterDay({
  trip,
  item,
  dayEvents,
  attsByEvent,
  imageDataUrls,
  sections,
  pageNumber,
  totalPages,
}: {
  trip: TravelTripResponse;
  item: TravelItemResponse;
  dayEvents: TravelEventResponse[];
  attsByEvent: Map<string, TravelAttachmentResponse[]>;
  imageDataUrls: Map<string, string>;
  sections: ExportSections;
  pageNumber: number;
  totalPages: number;
}) {
  const dayNum = item.dayNumber ?? 1;
  // Collect all event-level attachments for the photo strip at the bottom.
  const photoAttachments = sections.photos
    ? dayEvents.flatMap((ev) => attsByEvent.get(ev.eventId) || [])
    : [];
  return (
    <LetterFrame
      pageNumber={pageNumber}
      totalPages={totalPages}
      headerTitle={trip.title}
      headerRight={`Day ${dayNum}`}
    >
      <div
        style={{ borderBottom: '2px solid #e5e7eb', paddingBottom: '8px', marginBottom: '14px' }}
      >
        <div style={{ fontSize: '14px', color: '#2563eb', fontWeight: 500 }}>
          {formatDayDate(trip.startDate, dayNum)}
        </div>
        <div style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '2px' }}>
          {item.dayTitle || `Day ${dayNum}`}
        </div>
        {item.city && (
          <div style={{ fontSize: '15px', color: '#666', marginTop: '2px' }}>
            {item.city}
            {item.country ? `, ${item.country}` : ''}
          </div>
        )}
      </div>
      {sections.accommodations && item.accommodationName && (
        <div
          style={{
            marginBottom: '14px',
            padding: '10px 12px',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          🏨 <strong>{item.accommodationName}</strong>
          {item.accommodationAddress && (
            <div style={{ color: '#666', marginLeft: '22px', marginTop: '2px' }}>
              {item.accommodationAddress}
            </div>
          )}
        </div>
      )}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {dayEvents.length > 0 ? (
          dayEvents.map((ev) => {
            const color = getEventTypeColor(ev.eventType);
            return (
              <div
                key={ev.eventId}
                style={{
                  display: 'flex',
                  gap: '10px',
                  marginBottom: '10px',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: '56px',
                    textAlign: 'right',
                    fontSize: '14px',
                    color: '#888',
                    flexShrink: 0,
                    fontFamily: 'monospace',
                    paddingTop: '2px',
                  }}
                >
                  {ev.time ? formatTime(ev.time) : '—'}
                </div>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    marginTop: '8px',
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '15px', fontWeight: 500 }}>{ev.title}</span>
                  <span
                    style={{
                      fontSize: '11px',
                      marginLeft: '8px',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      backgroundColor: color + '20',
                      color,
                    }}
                  >
                    {EVENT_TYPE_LABELS[ev.eventType]}
                  </span>
                  {sections.notes && ev.notes && (
                    <div style={{ fontSize: '13px', color: '#666', marginTop: '3px' }}>
                      {ev.notes}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ fontSize: '14px', color: '#bbb', fontStyle: 'italic' }}>
            No events planned
          </div>
        )}
      </div>
      {photoAttachments.length > 0 && (
        <div style={{ marginTop: '12px', borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>
          <div
            style={{
              fontSize: '11px',
              color: '#888',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '6px',
            }}
          >
            Photos
          </div>
          <ExportAttachments
            eventAttachments={photoAttachments}
            imageDataUrls={imageDataUrls}
            thumbnail
          />
        </div>
      )}
    </LetterFrame>
  );
}

/** Letter-size transit page. */
function LetterTransit({
  trip,
  item,
  pageNumber,
  totalPages,
}: {
  trip: TravelTripResponse;
  item: TravelItemResponse;
  pageNumber: number;
  totalPages: number;
}) {
  return (
    <LetterFrame pageNumber={pageNumber} totalPages={totalPages} headerTitle={trip.title}>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>
          {item.transitMode ? TRANSIT_MODE_EMOJI[item.transitMode] : '↗'}
        </div>
        <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
          {item.transitFrom || '?'} → {item.transitTo || '?'}
        </div>
        {(item.transitDeparture || item.transitArrival) && (
          <div style={{ fontSize: '17px', color: '#666', marginTop: '14px' }}>
            {item.transitDeparture && <div>Departs {item.transitDeparture}</div>}
            {item.transitArrival && <div>Arrives {item.transitArrival}</div>}
          </div>
        )}
        {item.transitNotes && (
          <div
            style={{
              fontSize: '15px',
              color: '#666',
              marginTop: '18px',
              maxWidth: '500px',
            }}
          >
            {item.transitNotes}
          </div>
        )}
      </div>
    </LetterFrame>
  );
}

interface TravelExportImportProps {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  events: TravelEventResponse[];
  attachments?: TravelAttachmentResponse[];
  onImport: (data: Record<string, unknown>) => Promise<unknown>;
  shareEnabled?: boolean;
  onOpenShare?: () => void;
}

function slugify(title: string): string {
  return title.replace(/[^a-z0-9]/gi, '-').toLowerCase();
}

/** Upload a blob to Vault Documents under the user's Personal Holder. */
async function uploadToFias(blob: Blob, fileName: string, mimeType: string): Promise<void> {
  const memberships = await api.listMyVaultMemberships();
  if (!memberships.personalHolder) {
    throw new Error('Personal workspace not provisioned');
  }
  const file = new File([blob], fileName, { type: mimeType });
  await api.uploadFileToVault(file, memberships.personalHolder.holderId, {
    tags: ['travel', 'export'],
  });
}

/** Download a blob to the user's computer */
function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

// Browsers cap canvas width/height (~16,384px in Chrome; Safari/Firefox
// similar). Past it the canvas silently clips or comes back blank — so we
// guard against it explicitly rather than hand the user a broken image.
const MAX_CANVAS_DIMENSION = 16384;
// iOS Safari additionally caps total canvas *area* (~16.7M px on many
// devices) — a 1800×10000 canvas passes the per-dimension check but comes
// back with non-zero dimensions and silently blank pixels. Guard area too.
const MAX_CANVAS_AREA = 16_777_216;

/** Stitch multiple page canvases vertically into one tall canvas with separator gaps. */
export function stitchCanvasesVertically(canvases: HTMLCanvasElement[]): HTMLCanvasElement {
  if (canvases.length === 1) return canvases[0];
  const gap = 24;
  const maxWidth = Math.max(...canvases.map((c) => c.width));
  const totalHeight = canvases.reduce((sum, c) => sum + c.height, 0) + gap * (canvases.length - 1);
  if (
    totalHeight > MAX_CANVAS_DIMENSION ||
    maxWidth > MAX_CANVAS_DIMENSION ||
    totalHeight * maxWidth > MAX_CANVAS_AREA
  ) {
    throw new Error(
      'This trip is too long to stitch into one image. Choose Zip or individual delivery instead.',
    );
  }
  const out = document.createElement('canvas');
  out.width = maxWidth;
  out.height = totalHeight;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('Could not get 2d context');
  ctx.fillStyle = '#e5e7eb';
  ctx.fillRect(0, 0, maxWidth, totalHeight);
  let y = 0;
  for (const c of canvases) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, y, maxWidth, c.height);
    ctx.drawImage(c, Math.round((maxWidth - c.width) / 2), y);
    y += c.height + gap;
  }
  return out;
}

export function TravelExportImport({
  trip,
  items,
  events,
  attachments = [],
  onImport,
  shareEnabled = false,
  onOpenShare,
}: TravelExportImportProps) {
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [destination, setDestination] = useState<ExportDestination>('computer');
  const [jpgLayout, setJpgLayout] = useState<JpgLayout>('single');
  const [multiPageDelivery, setMultiPageDelivery] = useState<MultiPageDelivery>('zip');
  const [sections, setSections] = useState<ExportSections>(ALL_SECTIONS_ON);
  const [exporting, setExporting] = useState(false);
  const [quickJpgExporting, setQuickJpgExporting] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState<Map<string, string>>(new Map());
  const renderRef = useRef<HTMLDivElement>(null);

  const needsViewSelection = format === 'pdf' || format === 'jpg';
  const slug = slugify(trip.title);

  // When quickJpg is running, force single + all-on regardless of dialog state.
  const renderLayout: JpgLayout = quickJpgExporting ? 'single' : jpgLayout;
  const renderSections: ExportSections = quickJpgExporting ? ALL_SECTIONS_ON : sections;
  const pagePlan = buildPagePlan(renderLayout, renderSections, items);
  const totalPages = pagePlan.length;

  // Build the JSON export data
  const buildJsonData = useCallback(() => {
    const eventsByItem = buildEventsByItem(events);
    return {
      version: 2,
      startDate: trip.startDate,
      title: trip.title,
      items: items.map((item) => {
        if (item.itemType === 'day') {
          return {
            itemType: 'day',
            dayTitle: item.dayTitle,
            city: item.city,
            country: item.country,
            accommodationName: item.accommodationName,
            accommodationAddress: item.accommodationAddress,
            accommodationCheckin: item.accommodationCheckin,
            accommodationCheckout: item.accommodationCheckout,
            events: (eventsByItem.get(item.itemId) || []).map((ev) => ({
              time: ev.time,
              title: ev.title,
              eventType: ev.eventType,
              notes: ev.notes,
            })),
          };
        }
        return {
          itemType: 'transit',
          transitFrom: item.transitFrom,
          transitTo: item.transitTo,
          transitMode: item.transitMode,
          transitDeparture: item.transitDeparture,
          transitArrival: item.transitArrival,
          transitNotes: item.transitNotes,
        };
      }),
    };
  }, [trip, items, events]);

  /** Pre-fetch all image attachments as data URLs keyed by fileId */
  const prefetchImageAttachments = useCallback(async (): Promise<Map<string, string>> => {
    const dataUrls = new Map<string, string>();
    const imageAtts = attachments.filter((a) => a.mimeType && a.mimeType.startsWith('image/'));
    const fetchOne = async (att: (typeof imageAtts)[number]) => {
      try {
        const { downloadUrl } = await api.getDownloadUrl(att.fileId);
        const resp = await fetch(downloadUrl);
        // An expired/denied signed URL returns an error body — skip it rather
        // than baking the error bytes into the export as a broken "image".
        if (!resp.ok) throw new Error(`Attachment fetch failed (${resp.status})`);
        const blob = await resp.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          // Without onerror a failed read never settles this promise and the
          // export hangs on "Exporting…" forever — reject so the catch skips it.
          reader.onerror = () => reject(reader.error ?? new Error('Failed to read attachment'));
          reader.readAsDataURL(blob);
        });
        dataUrls.set(att.fileId, dataUrl);
      } catch {
        // skip
      }
    };
    // Bounded concurrency — a trip with dozens of image attachments shouldn't
    // fire every download at once and hit the browser's connection ceiling.
    const CONCURRENCY = 6;
    for (let i = 0; i < imageAtts.length; i += CONCURRENCY) {
      await Promise.all(imageAtts.slice(i, i + CONCURRENCY).map(fetchOne));
    }
    return dataUrls;
  }, [attachments]);

  /**
   * Render the offscreen container, then capture every [data-export-page] child
   * to its own canvas. Caller chooses what to do with the array (download as
   * single, zip, stitch, etc.).
   */
  const capturePages = useCallback(async (): Promise<HTMLCanvasElement[]> => {
    // Pre-fetch all images before rendering so they're available as data URLs
    const imageDataUrls = await prefetchImageAttachments();
    setPreloadedImages(imageDataUrls);

    // Wait for React to re-render with preloaded images
    await new Promise((r) => setTimeout(r, 500));

    if (!renderRef.current) throw new Error('Render container not ready');
    const container = renderRef.current;

    // Temporarily move on-screen for html2canvas (it needs layout)
    const prevLeft = container.style.left;
    container.style.left = '0px';
    container.style.zIndex = '-1';

    // Force light theme during capture so shadcn tokens (bg-card,
    // text-muted-foreground, etc.) inside TravelCalendarView resolve to legible
    // values. Without this, dark-mode users get a dark card with inherited
    // black text — i.e. black-on-black — because the export wrapper forces a
    // white background but cards underneath still use the dark theme variables.
    const rootEl = document.documentElement;
    const hadDark = rootEl.classList.contains('dark');
    if (hadDark) rootEl.classList.remove('dark');

    try {
      const html2canvas = (await import('html2canvas-pro')).default;
      const pageElements = Array.from(
        container.querySelectorAll<HTMLElement>('[data-export-page]'),
      );
      if (pageElements.length === 0) {
        throw new Error('No pages to capture');
      }
      const canvases: HTMLCanvasElement[] = [];
      for (const pageEl of pageElements) {
        const width = pageEl.offsetWidth;
        const height = pageEl.scrollHeight;
        // Pick the largest scale (up to 2×) that keeps the captured canvas under
        // the browser's max dimension AND max area (the iOS Safari limit). If
        // even 1× overflows, the page itself is taller than any canvas can
        // hold — fail with an actionable message instead of silently emitting
        // a clipped/blank image.
        const maxScale = Math.min(
          MAX_CANVAS_DIMENSION / Math.max(width, height),
          Math.sqrt(MAX_CANVAS_AREA / (width * height)),
        );
        if (maxScale < 1) {
          throw new Error(
            'This itinerary is too long to export as a single image. Use the Paginated layout, or choose Zip / individual delivery.',
          );
        }
        const canvas = await html2canvas(pageEl, {
          backgroundColor: '#ffffff',
          scale: Math.min(2, maxScale),
          useCORS: true,
          logging: false,
          width,
          windowWidth: width,
        });
        if (canvas.width === 0 || canvas.height === 0) {
          throw new Error('Captured canvas is empty');
        }
        canvases.push(canvas);
      }
      return canvases;
    } finally {
      if (hadDark) rootEl.classList.add('dark');
      container.style.left = prevLeft;
      container.style.zIndex = '';
    }
  }, [prefetchImageAttachments]);

  /** Deliver a multi-canvas JPG export per chosen delivery mode. */
  const deliverJpgPages = useCallback(
    async (canvases: HTMLCanvasElement[]) => {
      // Single canvas always downloads as one file regardless of delivery setting.
      if (canvases.length === 1) {
        const blob = await captureCanvasAsJpegBlob(canvases[0]);
        const fileName = `trip-${slug}.jpg`;
        if (destination === 'fias') {
          await uploadToFias(blob, fileName, 'image/jpeg');
          toast({ description: `Saved "${fileName}" to Fias Files` });
        } else {
          downloadBlob(blob, fileName);
        }
        return;
      }

      if (multiPageDelivery === 'stitched') {
        const stitched = stitchCanvasesVertically(canvases);
        const blob = await captureCanvasAsJpegBlob(stitched);
        const fileName = `trip-${slug}.jpg`;
        if (destination === 'fias') {
          await uploadToFias(blob, fileName, 'image/jpeg');
          toast({ description: `Saved "${fileName}" to Fias Files` });
        } else {
          downloadBlob(blob, fileName);
        }
        return;
      }

      if (multiPageDelivery === 'individual') {
        for (let i = 0; i < canvases.length; i++) {
          const blob = await captureCanvasAsJpegBlob(canvases[i]);
          const fileName = `trip-${slug}-page-${String(i + 1).padStart(2, '0')}.jpg`;
          if (destination === 'fias') {
            await uploadToFias(blob, fileName, 'image/jpeg');
          } else {
            downloadBlob(blob, fileName);
            // Brief delay so the browser doesn't suppress later downloads.
            await new Promise((r) => setTimeout(r, 200));
          }
        }
        toast({
          description:
            destination === 'fias'
              ? `Saved ${canvases.length} files to Fias Files`
              : `Downloaded ${canvases.length} JPG pages`,
        });
        return;
      }

      // zip
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      for (let i = 0; i < canvases.length; i++) {
        const blob = await captureCanvasAsJpegBlob(canvases[i]);
        const arrayBuffer = await blob.arrayBuffer();
        zip.file(`page-${String(i + 1).padStart(2, '0')}.jpg`, arrayBuffer);
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const fileName = `trip-${slug}.zip`;
      if (destination === 'fias') {
        await uploadToFias(zipBlob, fileName, 'application/zip');
        toast({ description: `Saved "${fileName}" to Fias Files` });
      } else {
        downloadBlob(zipBlob, fileName);
      }
    },
    [slug, destination, multiPageDelivery],
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      if (format === 'json') {
        const data = buildJsonData();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const fileName = `trip-${slug}.json`;

        if (destination === 'fias') {
          await uploadToFias(blob, fileName, 'application/json');
          toast({ description: `Saved "${fileName}" to Fias Files` });
        } else {
          downloadBlob(blob, fileName);
        }
      } else if (format === 'jpg') {
        const canvases = await capturePages();
        await deliverJpgPages(canvases);
      } else if (format === 'pdf') {
        const canvases = await capturePages();
        const { default: jsPDF } = await import('jspdf');
        const first = canvases[0];
        const visW = first.width / 2;
        const visH = first.height / 2;
        const pdf = new jsPDF({
          orientation: visW > visH ? 'landscape' : 'portrait',
          unit: 'px',
          format: [visW, visH],
        });
        pdf.addImage(await canvasToPdfImageBytes(first), 'JPEG', 0, 0, visW, visH);
        for (let i = 1; i < canvases.length; i++) {
          const c = canvases[i];
          const w = c.width / 2;
          const h = c.height / 2;
          pdf.addPage([w, h], w > h ? 'landscape' : 'portrait');
          pdf.addImage(await canvasToPdfImageBytes(c), 'JPEG', 0, 0, w, h);
        }

        const fileName = `trip-${slug}.pdf`;
        const pdfBlob = pdf.output('blob');
        if (destination === 'fias') {
          await uploadToFias(pdfBlob, fileName, 'application/pdf');
          toast({ description: `Saved "${fileName}" to Fias Files` });
        } else {
          downloadBlob(pdfBlob, fileName);
        }
      }
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), {
        component: 'TravelExportImport',
        action: 'export',
      });
      toast({
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
      setShowExportDialog(false);
    }
  }, [format, destination, slug, buildJsonData, capturePages, deliverJpgPages]);

  /** One-click JPG export: single tall image with everything on, saved to user's computer. */
  const handleQuickJpg = useCallback(async () => {
    setQuickJpgExporting(true);
    try {
      const canvases = await capturePages();
      const blob = await captureCanvasAsJpegBlob(canvases[0]);
      downloadBlob(blob, `trip-${slug}.jpg`);
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), {
        component: 'TravelExportImport',
        action: 'quick-jpg-export',
      });
      toast({
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setQuickJpgExporting(false);
    }
  }, [capturePages, slug]);

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        if (data.days && !data.items) {
          data.items = data.days.map((day: Record<string, unknown>) => ({
            ...day,
            itemType: 'day',
            dayTitle: day.title,
          }));
        }
        await onImport(data);
        setImportStatus('Imported successfully!');
        setTimeout(() => setImportStatus(null), 3000);
      } catch {
        setImportStatus('Import failed — invalid file');
        setTimeout(() => setImportStatus(null), 3000);
      }
    };
    input.click();
  };

  const noop = () => {};

  const toggleSection = (key: keyof ExportSections) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const exportDisabled =
    exporting || (needsViewSelection && !sections.calendar && !sections.itinerary);
  const showMultiPageDelivery = format === 'jpg' && jpgLayout !== 'single' && totalPages > 1;

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" title="Share, export or import trip">
            <Share2 className="h-3 w-3 mr-1" /> Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {shareEnabled && onOpenShare && (
            <>
              <DropdownMenuItem onSelect={onOpenShare}>
                <LinkIcon className="h-4 w-4 mr-2" /> Share link…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onSelect={handleQuickJpg} disabled={quickJpgExporting}>
            {quickJpgExporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ImageDown className="h-4 w-4 mr-2" />
            )}
            Save as JPG
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setShowExportDialog(true)}>
            <Download className="h-4 w-4 mr-2" /> Export trip…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleImport}>
            <Upload className="h-4 w-4 mr-2" /> Import trip…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {importStatus && (
        <span className="text-xs text-muted-foreground animate-pulse">{importStatus}</span>
      )}

      {/* Export dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Trip</DialogTitle>
            <DialogDescription>
              Choose the format, layout, and content to include.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto">
            {/* Format */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Format
              </label>
              <Select value={format} onValueChange={(v) => setFormat(v as ExportFormat)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF (printable)</SelectItem>
                  <SelectItem value="jpg">JPG (image)</SelectItem>
                  <SelectItem value="json">JSON (data)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Destination */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Save to
              </label>
              <Select
                value={destination}
                onValueChange={(v) => setDestination(v as ExportDestination)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="computer">My Computer</SelectItem>
                  <SelectItem value="fias">Fias Files</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* JPG-only layout */}
            {format === 'jpg' && (
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Layout
                </label>
                <div className="space-y-1.5">
                  {(
                    [
                      {
                        v: 'single',
                        label: 'Single tall image',
                        hint: 'One long JPG, all content stacked',
                      },
                      {
                        v: 'paginated',
                        label: 'List, paginated',
                        hint: 'One page per day, list styling',
                      },
                      {
                        v: 'letter',
                        label: 'Letter (8.5 × 11)',
                        hint: 'Print-style pages, cover + day pages',
                      },
                    ] as const
                  ).map((opt) => (
                    <label
                      key={opt.v}
                      className="flex items-start gap-2 cursor-pointer p-1.5 rounded hover:bg-muted/50"
                    >
                      <input
                        type="radio"
                        name="jpg-layout"
                        value={opt.v}
                        checked={jpgLayout === opt.v}
                        onChange={() => setJpgLayout(opt.v)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm">{opt.label}</div>
                        <div className="text-xs text-muted-foreground">{opt.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Multi-page delivery (JPG only, multi-page layouts) */}
            {showMultiPageDelivery && (
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Multi-page delivery ({totalPages} pages)
                </label>
                <div className="space-y-1.5">
                  {(
                    [
                      { v: 'zip', label: 'ZIP bundle', hint: 'One .zip with page-NN.jpg files' },
                      {
                        v: 'individual',
                        label: 'Individual downloads',
                        hint: 'Separate JPG per page',
                      },
                      {
                        v: 'stitched',
                        label: 'Stitched into one JPG',
                        hint: 'All pages re-combined into a single tall image',
                      },
                    ] as const
                  ).map((opt) => (
                    <label
                      key={opt.v}
                      className="flex items-start gap-2 cursor-pointer p-1.5 rounded hover:bg-muted/50"
                    >
                      <input
                        type="radio"
                        name="jpg-delivery"
                        value={opt.v}
                        checked={multiPageDelivery === opt.v}
                        onChange={() => setMultiPageDelivery(opt.v)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm">{opt.label}</div>
                        <div className="text-xs text-muted-foreground">{opt.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Section toggles (PDF + JPG) */}
            {needsViewSelection && (
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Include
                </label>
                <div className="grid grid-cols-2 gap-1.5">
                  {SECTION_LABELS.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={sections[key]}
                        onChange={() => toggleSection(key)}
                        className="rounded border-input"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={exportDisabled} onClick={handleExport}>
              {exporting ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Exporting...
                </>
              ) : (
                <>
                  <Download className="h-3 w-3 mr-1" /> Export
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Render container for PDF/JPG capture — positioned off-screen to the left
           but with visibility preserved so html2canvas can read computed styles.
           The capture path temporarily strips `.dark` from <html> so shadcn
           tokens used by descendants (TravelCalendarView's bg-card etc.) resolve
           to their light-theme values. */}
      {((showExportDialog && needsViewSelection) || quickJpgExporting) && (
        <div
          ref={renderRef}
          style={{
            position: 'absolute',
            left: '-10000px',
            top: 0,
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <ExportPages
            trip={trip}
            items={items}
            events={events}
            attachments={attachments}
            sections={renderSections}
            pagePlan={pagePlan}
            imageDataUrls={preloadedImages}
            onSelectItem={noop}
            onSwitchToPlanner={noop}
          />
        </div>
      )}
    </div>
  );
}

/** Renders every page in the plan back-to-back inside the offscreen container.
 *  Each page is marked with `data-export-page` so the capture loop can pick it up. */
function ExportPages({
  trip,
  items,
  events,
  attachments,
  sections,
  pagePlan,
  imageDataUrls,
  onSelectItem,
  onSwitchToPlanner,
}: {
  trip: TravelTripResponse;
  items: TravelItemResponse[];
  events: TravelEventResponse[];
  attachments: TravelAttachmentResponse[];
  sections: ExportSections;
  pagePlan: PageDescriptor[];
  imageDataUrls: Map<string, string>;
  onSelectItem: () => void;
  onSwitchToPlanner: () => void;
}) {
  const eventsByItem = buildEventsByItem(events);
  const attsByEvent = buildAttsByEvent(attachments);
  const totalPages = pagePlan.length;
  const dayCount = items.filter((i) => i.itemType === 'day').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {pagePlan.map((page, idx) => {
        const pageNumber = idx + 1;
        if (page.kind === 'single') {
          return (
            <div
              key="single"
              data-export-page
              style={{
                width: `${LIST_W}px`,
                padding: `${LIST_PAD}px`,
                backgroundColor: '#ffffff',
                color: '#000000',
                boxSizing: 'border-box',
              }}
            >
              <h1 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
                {trip.title}
              </h1>
              <p style={{ fontSize: '18px', color: '#666', marginBottom: '32px' }}>
                {dayCount} days &middot; {events.length} events
              </p>
              {sections.calendar && (
                <div style={{ marginBottom: sections.itinerary ? '32px' : 0 }}>
                  <h2
                    style={{
                      fontSize: '22px',
                      fontWeight: '600',
                      marginBottom: '16px',
                      borderBottom: '2px solid #e5e7eb',
                      paddingBottom: '10px',
                    }}
                  >
                    Calendar
                  </h2>
                  <TravelCalendarView
                    trip={trip}
                    items={items}
                    events={events}
                    attachments={attachments}
                    onSelectItem={onSelectItem}
                    onSwitchToPlanner={onSwitchToPlanner}
                  />
                </div>
              )}
              {sections.itinerary && (
                <div>
                  <h2
                    style={{
                      fontSize: '22px',
                      fontWeight: '600',
                      marginBottom: '16px',
                      borderBottom: '2px solid #e5e7eb',
                      paddingBottom: '10px',
                    }}
                  >
                    Itinerary
                  </h2>
                  {items.map((item) => {
                    if (item.itemType === 'transit') {
                      return sections.transit ? <TransitRow key={item.itemId} item={item} /> : null;
                    }
                    return (
                      <DayCard
                        key={item.itemId}
                        trip={trip}
                        item={item}
                        dayEvents={eventsByItem.get(item.itemId) || []}
                        attsByEvent={attsByEvent}
                        imageDataUrls={imageDataUrls}
                        sections={sections}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        }
        if (page.kind === 'paginated-calendar') {
          return (
            <div
              key="paginated-calendar"
              data-export-page
              style={{
                width: `${LIST_W}px`,
                padding: `${LIST_PAD}px`,
                backgroundColor: '#ffffff',
                color: '#000000',
                boxSizing: 'border-box',
              }}
            >
              <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '6px' }}>
                {trip.title}
              </h1>
              <p style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
                {formatDateRange(trip.startDate, dayCount)} · {dayCount} days · {events.length}{' '}
                events
              </p>
              <h2
                style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '14px',
                  borderBottom: '2px solid #e5e7eb',
                  paddingBottom: '8px',
                }}
              >
                Calendar
              </h2>
              <TravelCalendarView
                trip={trip}
                items={items}
                events={events}
                attachments={attachments}
                onSelectItem={onSelectItem}
                onSwitchToPlanner={onSwitchToPlanner}
              />
              <div
                style={{
                  fontSize: '11px',
                  color: '#aaa',
                  textAlign: 'center',
                  marginTop: '20px',
                }}
              >
                {pageNumber} / {totalPages}
              </div>
            </div>
          );
        }
        if (page.kind === 'paginated-day') {
          return (
            <div
              key={page.item.itemId}
              data-export-page
              style={{
                width: `${LIST_W}px`,
                padding: `${LIST_PAD}px`,
                backgroundColor: '#ffffff',
                color: '#000000',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  color: '#888',
                  marginBottom: '14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid #e5e7eb',
                  paddingBottom: '6px',
                }}
              >
                <span>{trip.title}</span>
                <span>
                  Day {page.item.dayNumber ?? '?'} · {pageNumber} / {totalPages}
                </span>
              </div>
              <DayCard
                trip={trip}
                item={page.item}
                dayEvents={eventsByItem.get(page.item.itemId) || []}
                attsByEvent={attsByEvent}
                imageDataUrls={imageDataUrls}
                sections={sections}
              />
            </div>
          );
        }
        if (page.kind === 'paginated-transit') {
          return (
            <div
              key={page.item.itemId}
              data-export-page
              style={{
                width: `${LIST_W}px`,
                padding: `${LIST_PAD}px`,
                backgroundColor: '#ffffff',
                color: '#000000',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  color: '#888',
                  marginBottom: '14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid #e5e7eb',
                  paddingBottom: '6px',
                }}
              >
                <span>{trip.title}</span>
                <span>
                  Transit · {pageNumber} / {totalPages}
                </span>
              </div>
              <TransitRow item={page.item} />
            </div>
          );
        }
        if (page.kind === 'letter-cover') {
          return (
            <LetterCover
              key="letter-cover"
              trip={trip}
              dayCount={dayCount}
              eventCount={events.length}
              pageNumber={pageNumber}
              totalPages={totalPages}
            />
          );
        }
        if (page.kind === 'letter-calendar') {
          return (
            <LetterFrame
              key="letter-calendar"
              pageNumber={pageNumber}
              totalPages={totalPages}
              headerTitle={trip.title}
              headerRight="Calendar"
            >
              <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '14px' }}>
                Calendar
              </h2>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <TravelCalendarView
                  trip={trip}
                  items={items}
                  events={events}
                  attachments={attachments}
                  onSelectItem={onSelectItem}
                  onSwitchToPlanner={onSwitchToPlanner}
                />
              </div>
            </LetterFrame>
          );
        }
        if (page.kind === 'letter-day') {
          return (
            <LetterDay
              key={page.item.itemId}
              trip={trip}
              item={page.item}
              dayEvents={eventsByItem.get(page.item.itemId) || []}
              attsByEvent={attsByEvent}
              imageDataUrls={imageDataUrls}
              sections={sections}
              pageNumber={pageNumber}
              totalPages={totalPages}
            />
          );
        }
        if (page.kind === 'letter-transit') {
          return (
            <LetterTransit
              key={page.item.itemId}
              trip={trip}
              item={page.item}
              pageNumber={pageNumber}
              totalPages={totalPages}
            />
          );
        }
        return null;
      })}
    </div>
  );
}
